import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import {
	Box,
	AppBar,
	Toolbar,
	Typography,
	Button,
	IconButton,
	Menu,
	MenuItem,
	FormControl,
	InputLabel,
	Select,
	Chip,
	Paper,
	Container,
	useTheme,
	useMediaQuery,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Alert,
	CircularProgress,
	Skeleton,
} from "@mui/material";
import {
	Logout,
	AccountCircle,
	FilterList,
	Clear,
	FileDownload,
	ViewKanban,
	ViewList,
	Chat as ChatIcon,
	AdminPanelSettings,
	Add,
	AccessTime as TimeIcon,
} from "@mui/icons-material";
import { DarkMode, LightMode } from "@mui/icons-material";
import { Lock } from "@mui/icons-material";
import {
	DndContext,
	DragEndEvent,
	closestCorners,
	KeyboardSensor,
	PointerSensor,
	TouchSensor,
	useSensor,
	useSensors,
	DragOverlay,
	DragStartEvent,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import { usePermissions } from "../contexts/PermissionsContext";
import { useTrialDays } from "../hooks/useTrialDays";
import { Lead, User, LeadQuality, LeadStatus, Status } from "../types";
import KanbanColumn from "../components/StyledKanbanColumn";
import LeadCard from "../components/MinimalLeadCard";
import LeadDetailModal from "../components/LeadDetailModal";
import AssignLeadModal from "../components/AssignLeadModal";
import LeadAssignmentConfirmationModal from "../components/LeadAssignmentConfirmationModal";
import TransferLeadModal from "../components/TransferLeadModal";
import CreateLeadModal from "../components/CreateLeadModal";
import DeleteLeadModal from "../components/DeleteLeadModal";
import { exportLeadsToExcel } from "../utils/excelExport";
import LeadAssignmentTracker from "../utils/leadAssignmentTracker";
import {
	fetchSalesUsers,
	fetchSalesUsersByManager,
	updateLeadStatus,
	assignLead,
	fetchAssignedLeadsPaged,
	updatePassword,
	deleteLead,
	BackendUser,
	getLogoByDomain,
	fetchAccountAgents,
	AgentInfo,
	getAllActiveAgentUsers,
	AgentUserRS,
} from "../utils/api";
import { statusApi } from "../utils/statusApi";
import LeadQualityIcon from "../components/LeadQualityIcon";

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import NotificationCenter from "../components/NotificationCenter";
import WelcomeAssistantModal from "../components/WelcomeAssistantModal";
import PlanInactiveModal from "../components/PlanInactiveModal";
import PlanInactiveAlert from "../components/PlanInactiveAlert";
import FirstTimeWelcomeModal from "../components/FirstTimeWelcomeModal";
import { useThemeMode } from "../contexts/ThemeContext";
import { mapBackendRoleToFrontend, isSalesRole, isManagerRole, isAdminLike } from "../utils/roleMapping";
import { useWhiteLabelHiding } from "../hooks/useWhiteLabelHiding";
import LanguageSelector from "../components/LanguageSelector";
import { useTranslation } from "react-i18next";

const DashboardPage: React.FC = () => {
	const { user, logout, permissions, isAuthenticated, isInitializing } = useAuth();
	const { t } = useTranslation();

	// Debug log
	console.log("DashboardPage - User state:", {
		user: user,
		isAuthenticated: isAuthenticated,
		isInitializing: isInitializing,
		userRole: user?.role,
		isWhiteLabel: user?.isWhiteLabel,
		shouldShowAdmin: isAdminLike(user?.role || ""),
	});
	const { mode, toggleMode } = useThemeMode();
	const { onLeadUpdate, offLeadUpdate } = useNotifications();
	const { canShowAlert, canViewMenu } = usePermissions();
	const { trialData } = useTrialDays();
	const { shouldHidePaymentElements } = useWhiteLabelHiding();
	const { leadId } = useParams<{ leadId: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	const [welcomeOpen, setWelcomeOpen] = useState(false);
	const [planInactiveModalOpen, setPlanInactiveModalOpen] = useState(false);
	const [planInactiveAlertOpen, setPlanInactiveAlertOpen] = useState(false);
	const [firstTimeWelcomeOpen, setFirstTimeWelcomeOpen] = useState(false);
	const [leads, setLeads] = useState<Lead[]>([]);
	const [users, setUsers] = useState<User[]>([]);
	const [agents, setAgents] = useState<AgentInfo[]>([]);
	const [agentUsers, setAgentUsers] = useState<AgentUserRS[]>([]);
	const [statuses, setStatuses] = useState<Status[]>([]);
	const [statusesLoading, setStatusesLoading] = useState<boolean>(true);
	const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [currentTab, setCurrentTab] = useState(0);
	const [qualityFilter, setQualityFilter] = useState<LeadQuality | "all">("all");
	const [dateFilter, setDateFilter] = useState<"all" | "today" | "week" | "month" | "custom">("all");
	const [customDateOpen, setCustomDateOpen] = useState(false);
	const [customStartDate, setCustomStartDate] = useState<string>("");
	const [customEndDate, setCustomEndDate] = useState<string>("");
	const [sellerFilter, setSellerFilter] = useState<string>("all");
	const [agentFilter, setAgentFilter] = useState<string>("all");
	const [nameFilter, setNameFilter] = useState<string>("");
	const [phoneFilter, setPhoneFilter] = useState<string>("");
	const [activeLead, setActiveLead] = useState<Lead | null>(null);
	const [assignModalOpen, setAssignModalOpen] = useState(false);
	const [leadToAssign, setLeadToAssign] = useState<Lead | null>(null);
	const [transferModalOpen, setTransferModalOpen] = useState(false);
	const [leadToTransfer, setLeadToTransfer] = useState<Lead | null>(null);
	const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
	const [leadForConfirmation, setLeadForConfirmation] = useState<Lead | null>(null);
	const [createLeadModalOpen, setCreateLeadModalOpen] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
	const [dynamicLogo, setDynamicLogo] = useState<string | null>(null);
	const [logoLoading, setLogoLoading] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [viewMode, setViewMode] = useState<"kanban" | "list">("kanban");
	// Change password modal state
	const [changePwdOpen, setChangePwdOpen] = useState(false);
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [changePwdLoading, setChangePwdLoading] = useState(false);
	const [changePwdError, setChangePwdError] = useState("");
	const [changePwdSuccess, setChangePwdSuccess] = useState("");
	const [passwordValidationError, setPasswordValidationError] = useState("");
	// Assigned leads pagination state
	const [assignedGlobalHasMore, setAssignedGlobalHasMore] = useState<boolean>(true);
	const [assignedGlobalLoading, setAssignedGlobalLoading] = useState<boolean>(false);
	const [assignedStatusHasMore, setAssignedStatusHasMore] = useState<Record<Status["id"], boolean>>({});
	const [assignedStatusLoading, setAssignedStatusLoading] = useState<Record<Status["id"], boolean>>({});

	// Função para verificar se o domínio é permitido (localhost ou platform)
	const isAllowedDomain = () => {
		const currentDomain = window.location.origin;
		return currentDomain === "http://localhost:3000" || currentDomain === "https://platform.liatalk.ai";
	};

	// Função para extrair domínio da URL atual
	const getCurrentDomain = () => {
		const currentUrl = window.location.hostname;
		return currentUrl;
	};

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: 200,
				tolerance: 8,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	useEffect(() => {
		// Open welcome assistant on initial navigation if requested
		const shouldOpen = (location.state as any)?.showWelcome;
		if (shouldOpen) {
			setWelcomeOpen(true);
			// Clear state to avoid reopening on future navigations
			navigate(location.pathname, { replace: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Mostrar modal de boas-vindas no primeiro acesso
	useEffect(() => {
		const shouldShowWelcome = (location.state as any)?.showWelcome;
		const hasSeenFirstTimeWelcome = localStorage.getItem("hasSeenFirstTimeWelcome");

		if (shouldShowWelcome && !hasSeenFirstTimeWelcome && !shouldHidePaymentElements) {
			setFirstTimeWelcomeOpen(true);
			localStorage.setItem("hasSeenFirstTimeWelcome", "true");
			// Clear state to avoid reopening on future navigations
			navigate(location.pathname, { replace: true });
		}
	}, [location.state, navigate, location.pathname, shouldHidePaymentElements]);

	// Verificar se o plano está inativo
	useEffect(() => {
		if (
			user &&
			user.isPlanActive === false &&
			canShowAlert("showPlanInactiveAlerts") &&
			!shouldHidePaymentElements
		) {
			setPlanInactiveAlertOpen(true);
			// Mostrar modal apenas uma vez por login
			const hasSeenPlanModal = localStorage.getItem("hasSeenPlanInactiveModal");
			if (!hasSeenPlanModal && canShowAlert("showPaymentAlerts")) {
				setPlanInactiveModalOpen(true);
				localStorage.setItem("hasSeenPlanInactiveModal", "true");
			}
		} else if (user && user.isPlanActive === true) {
			// Se o plano estiver ativo, esconder os alertas
			setPlanInactiveAlertOpen(false);
			setPlanInactiveModalOpen(false);
		}
	}, [user, isAuthenticated, canShowAlert, shouldHidePaymentElements]);

	// Carregar logo dinamicamente baseado no domínio
	useEffect(() => {
		const loadDynamicLogo = async () => {
			const domain = getCurrentDomain();

			// Só tenta carregar logo se não for localhost:3000 ou platform.liatalk.ai
			if (domain !== "localhost" && domain !== "platform.liatalk.ai") {
				setLogoLoading(true);
				try {
					const logoData = await getLogoByDomain(domain);
					if (logoData?.logoUrl) {
						setDynamicLogo(logoData.logoUrl);
					}
				} catch (error) {
					console.error("Error loading dynamic logo:", error);
				} finally {
					setLogoLoading(false);
				}
			}
		};

		loadDynamicLogo();
	}, []);

	useEffect(() => {
		let isMounted = true;
		const loadLeads = async () => {
			try {
				let backendLeads;
				// Assigned leads initial fetch (most recent 5) with server-side filters
				const { startDateISO, endDateISO } = (() => {
					const now = new Date();
					const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
					if (dateFilter === "today")
						return {
							startDateISO: startOfToday.toISOString(),
							endDateISO: undefined as string | undefined,
						};
					if (dateFilter === "week")
						return {
							startDateISO: new Date(startOfToday.getTime() - 7 * 86400000).toISOString(),
							endDateISO: undefined,
						};
					if (dateFilter === "month")
						return {
							startDateISO: new Date(startOfToday.getTime() - 30 * 86400000).toISOString(),
							endDateISO: undefined,
						};
					if (dateFilter === "custom")
						return {
							startDateISO: customStartDate ? new Date(customStartDate).toISOString() : undefined,
							endDateISO: customEndDate ? new Date(customEndDate).toISOString() : undefined,
						};
					return { startDateISO: undefined, endDateISO: undefined };
				})();
				const rs = await fetchAssignedLeadsPaged({
					skip: 0,
					take: 50,
					status: undefined,
					quality: qualityFilter,
					sellerId: sellerFilter !== "all" ? sellerFilter : undefined,
					agentId: agentFilter !== "all" ? agentFilter : undefined,
					name: nameFilter || undefined,
					phone: phoneFilter || undefined,
					startDate: startDateISO,
					endDate: endDateISO,
				});
				backendLeads = rs;

				setAssignedGlobalHasMore((rs?.length || 0) === 50);
				// reset per-status hasMore dynamically based on current statuses
				setAssignedStatusHasMore(Object.fromEntries(statuses.map((s) => [s.id, true])));
				if (!isMounted) return;
				const mapped = (backendLeads || []).map((l) => {
					// Find the status in our statuses array
					// First try to find by statusId, then by status name
					let statusInfo = statuses.find((s) => s.id === (l as any).statusId);
					if (!statusInfo) {
						statusInfo = statuses.find((s) => s.name === l.status);
					}

					const statusId = (l as any).statusId || statusInfo?.id;
					const statusName = statusInfo?.name || l.status;
					const statusColor = statusInfo?.color;

					return {
						_id: { $oid: l.id },
						name: l.name,
						agentId: String(l.agentId),
						phoneNumber: l.phoneNumber,
						email: l.email,
						agentPhone: l.agentPhone || "",
						interested: l.interested || "",
						resumeAI: l.resumeAI,
						leadQuality: l.leadQuality as any as LeadQuality | undefined,
						status: ((): LeadStatus | undefined => {
							const raw = String(l.status || "").toLowerCase();
							const s = raw.replace(/\s+/g, "_");
							if (s === "novo") return "novo";
							if (
								s === "contato_inicial" ||
								s === "contato-inicial" ||
								s === "contatoinicial" ||
								s === "emandamento" ||
								s === "em_andamento"
							)
								return "contato_inicial";
							if (s === "qualificado") return "qualificado";
							if (s === "proposta") return "proposta";
							if (s === "negociacao" || s === "negociação") return "negociacao";
							if (s === "fechado" || s === "finalizado") return "fechado";
							if (s === "perdido") return "perdido";
							return undefined;
						})(),
						statusId: statusId,
						statusName: statusName,
						statusColor: statusColor,
						assignedTo: l.assignedTo || undefined,
						assignedToName: l.assignedToName || undefined,
						company: "",
						observations: "",
						observacoes: l.observacoes as any,
						campaignInfo: undefined,
						creationTime: { $date: l.creationTime || new Date().toISOString() },
						rating: l.rating || undefined,
					};
				}) as Lead[];
				setLeads(mapped);
			} catch (e) {
				// fallback: keep existing leads state empty
			}
		};
		loadLeads();
		return () => {
			isMounted = false;
		};
	}, [
		qualityFilter,
		sellerFilter,
		agentFilter,
		dateFilter,
		customStartDate,
		customEndDate,
		nameFilter,
		phoneFilter,
		statuses,
	]);

	useEffect(() => {
		let isMounted = true;
		const loadUsers = async () => {
			try {
				const sales = await (isManagerRole(user?.role || "") ? fetchSalesUsersByManager() : fetchSalesUsers());
				if (isMounted) {
					// Map backend users to frontend format with proper role mapping
					const mappedUsers = sales.map((backendUser: BackendUser) => ({
						id: backendUser.id,
						name: backendUser.name,
						email: backendUser.email,
						phone: "", // BackendUser doesn't have phone field
						role: mapBackendRoleToFrontend(backendUser.role),
						managerId: backendUser.managerId,
						teamMembers: backendUser.teamMembers,
					}));
					const agentUsersData = await getAllActiveAgentUsers();
					console.log("DashboardPage - agentUsersData loaded:", agentUsersData);
					setAgentUsers(agentUsersData);
					setUsers(mappedUsers);
				}
			} catch (e) {
				// silently ignore for now; UI can still operate without user list for filters
			}
		};
		loadUsers();
		return () => {
			isMounted = false;
		};
	}, [user?.role]); // This dependency is fine as it should only change when user role changes

	useEffect(() => {
		let isMounted = true;
		const loadAgentUsers = async () => {
			try {
				const agentUsersData = await getAllActiveAgentUsers();
				if (isMounted) {
					setAgentUsers(agentUsersData);
				}
			} catch (e) {
				console.error("Failed to load agent users:", e);
				// silently ignore for now; UI can still operate without agent users list for filters
			}
		};
		loadAgentUsers();
		return () => {
			isMounted = false;
		};
	}, []); // Load agent users once on mount

	useEffect(() => {
		let isMounted = true;
		const loadAgents = async () => {
			try {
				const agentsData = await fetchAccountAgents();
				if (isMounted) {
					setAgents(agentsData);
				}
			} catch (e) {
				console.error("Failed to load agents:", e);
				// silently ignore for now; UI can still operate without agents list for filters
			}
		};
		loadAgents();
		return () => {
			isMounted = false;
		};
	}, []); // Load agents once on mount

	useEffect(() => {
		let isMounted = true;
		const loadStatuses = async () => {
			try {
				setStatusesLoading(true);
				// Use the user's organization ID, fallback to user ID if not available
				const data = await statusApi.getStatuses();
				if (isMounted) {
					setStatuses(data.sort((a, b) => a.order - b.order));
					setStatusesLoading(false);
				}
			} catch (e) {
				// If statuses fail to load, fall back to hardcoded statuses
				if (isMounted) {
					setStatuses([
						{ id: "novo", name: "Novo", color: "#35e87c", order: 1, isDefault: true },
						{ id: "contato_inicial", name: "Contato Inicial", color: "#0288d1", order: 2, isDefault: true },
						{ id: "qualificado", name: "Qualificado", color: "#388e3c", order: 3, isDefault: true },
						{ id: "proposta", name: "Proposta", color: "#fbc02d", order: 4, isDefault: true },
						{ id: "negociacao", name: "Negociação", color: "#f57c00", order: 5, isDefault: true },
						{ id: "fechado", name: "Fechado", color: "#388e3c", order: 6, isDefault: true },
						{ id: "perdido", name: "Perdido", color: "#d32f2f", order: 7, isDefault: true },
					]);
					setStatusesLoading(false);
				}
			}
		};
		loadStatuses();
		return () => {
			isMounted = false;
		};
	}, [user?.id]);

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleLogout = () => {
		handleMenuClose();
		logout();
	};

	const handleChangePassword = () => {
		handleMenuClose();
		setChangePwdOpen(true);
	};

	const handleCloseChangePwd = () => {
		if (changePwdLoading) return;
		setChangePwdOpen(false);
		setCurrentPassword("");
		setNewPassword("");
		setConfirmPassword("");
		setChangePwdError("");
		setChangePwdSuccess("");
		setPasswordValidationError("");
	};

	const isFormValid = useMemo(() => {
		return (
			currentPassword.trim() !== "" &&
			newPassword.trim() !== "" &&
			confirmPassword.trim() !== "" &&
			newPassword.length >= 6
		);
	}, [currentPassword, newPassword, confirmPassword]);

	const handleSubmitChangePwd = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!user) return;
		setChangePwdError("");
		setChangePwdSuccess("");
		setPasswordValidationError("");

		if (newPassword !== confirmPassword) {
			setPasswordValidationError(t("dashboard.passwordsDontMatch"));
			return;
		}

		if (!isFormValid) {
			return;
		}

		setChangePwdLoading(true);
		try {
			await updatePassword(user.id, currentPassword, newPassword);
			setChangePwdSuccess(t("dashboard.passwordUpdated"));
			toast.success(t("dashboard.passwordUpdated"));
			setTimeout(() => {
				handleCloseChangePwd();
			}, 800);
		} catch (err: any) {
			let errorMessage = t("dashboard.passwordUpdateError");

			if (err?.message?.includes("400") || err?.response?.status === 400) {
				errorMessage = t("dashboard.incorrectPassword");
			} else if (err?.message) {
				errorMessage = err.message;
			}

			setChangePwdError(errorMessage);
			toast.error(errorMessage);
		} finally {
			setChangePwdLoading(false);
		}
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setCurrentTab(newValue);
	};

	const handleDragStart = (event: DragStartEvent) => {
		const { active } = event;
		const lead = leads.find((lead) => lead._id.$oid === active.id);
		setActiveLead(lead || null);
	};

	const handleDragEnd = (event: DragEndEvent) => {
		setActiveLead(null);
		const { active, over } = event;

		if (!over) return;

		const activeId = active.id as string;
		const overId = over.id as string;

		const activeLead = leads.find((lead) => lead._id.$oid === activeId);
		if (!activeLead) return;

		// Determine the target column: can be a column id OR another card id inside a column
		let targetStatusId: string | undefined = undefined;
		if (statuses.some((s) => s.id === overId)) {
			targetStatusId = overId;
		} else {
			const overLead = leads.find((lead) => lead._id.$oid === overId);
			if (overLead && (overLead.statusId || overLead.status)) {
				targetStatusId = overLead.statusId || overLead.status;
			}
		}

		if (!targetStatusId || (activeLead.statusId || activeLead.status) === targetStatusId) return;

		// Find the target status info to get the correct status name
		const targetStatusInfo = statuses.find((s) => s.id === targetStatusId);
		const targetStatusName = targetStatusInfo?.name || targetStatusId;

		const prevLeads = leads;
		const updatedLead: Lead = {
			...activeLead,
			statusId: targetStatusId,
			status: targetStatusName as LeadStatus, // Use the status name, not ID
			statusName: targetStatusName,
			statusColor: targetStatusInfo?.color,
		};

		setLeads((prevLeads) => prevLeads.map((lead) => (lead._id.$oid === activeId ? updatedLead : lead)));

		updateLeadStatus(activeId, targetStatusName as LeadStatus, targetStatusId).catch(() => {
			setLeads(prevLeads);
			toast.error(t("dashboard.updateStatusError"));
		});
	};

	const handleUpdateLead = (updatedLead: Lead) => {
		setLeads((prevLeads) => prevLeads.map((lead) => (lead._id.$oid === updatedLead._id.$oid ? updatedLead : lead)));
	};

	const handleExportToExcel = async () => {
		const leadsToExport = getFilteredLeads();
		await exportLeadsToExcel(
			leadsToExport,
			users,
			agentUsers,
			agents,
			`leads_${new Date().toISOString().split("T")[0]}.xlsx`
		);
	};

	const handleAssignLead = async (leadId: string) => {
		if (!user) return;

		// For vendedor users, show confirmation modal with hourly limits
		if (isSalesRole(user.role || "")) {
			const lead = leads.find((l) => l._id.$oid === leadId);
			if (lead) {
				setLeadForConfirmation(lead);
				setConfirmationModalOpen(true);
			}
			return;
		}

		// For non-vendedor users, assign directly
		try {
			await assignLead(leadId, user.id);
			setLeads((prevLeads) =>
				prevLeads.map((lead) => (lead._id.$oid === leadId ? { ...lead, assignedTo: user.id } : lead))
			);
			toast.success(t("dashboard.leadAssignedSuccess"));
		} catch {
			toast.error(t("dashboard.assignLeadError"));
		}
	};

	const handleAssignLeadFromModal = (lead: Lead) => {
		handleAssignLead(lead._id.$oid);
	};

	const handleOpenAssignModal = (lead: Lead) => {
		setLeadToAssign(lead);
		setAssignModalOpen(true);
	};

	const handleCloseAssignModal = () => {
		setLeadToAssign(null);
		setAssignModalOpen(false);
	};

	const handleAssignToUser = async (leadId: string, userId: string) => {
		try {
			await assignLead(leadId, userId);
			setLeads((prevLeads) =>
				prevLeads.map((lead) => (lead._id.$oid === leadId ? { ...lead, assignedTo: userId } : lead))
			);
			toast.success(t("dashboard.leadAssignedSuccess"));
		} catch {
			toast.error(t("dashboard.assignLeadError"));
		}
	};

	const handleTransferLead = async (leadId: string, newUserId: string) => {
		const prevLeads = leads;
		const prevSelectedLead = selectedLead;

		// Buscar o nome do novo usuário responsável
		const newAssignedUser = users.find((u) => u.id === newUserId);
		const newAssignedAgentUser = agentUsers.find((au) => au.userId === newUserId);
		const newAssignedUserName = newAssignedUser?.name || newAssignedAgentUser?.userName || "";

		// Atualizar lista de leads
		setLeads((prev) =>
			prev.map((l) =>
				l._id.$oid === leadId
					? {
							...l,
							assignedTo: newUserId,
							assignedToName: newAssignedUserName,
					  }
					: l
			)
		);

		// Atualizar lead selecionado se for o mesmo que está sendo transferido
		if (selectedLead && selectedLead._id.$oid === leadId) {
			setSelectedLead((prev) =>
				prev
					? {
							...prev,
							assignedTo: newUserId,
							assignedToName: newAssignedUserName,
					  }
					: null
			);
		}

		try {
			await assignLead(leadId, newUserId);
			toast.success(t("dashboard.leadTransferredSuccess"));
		} catch (e) {
			// Reverter mudanças em caso de erro
			setLeads(prevLeads);
			setSelectedLead(prevSelectedLead);
			toast.error(t("dashboard.transferLeadError"));
		}
	};

	const handleOpenTransferModal = (lead: Lead) => {
		setLeadToTransfer(lead);
		setTransferModalOpen(true);
	};

	const handleCloseTransferModal = () => {
		setLeadToTransfer(null);
		setTransferModalOpen(false);
	};

	const handleConfirmLeadAssignment = async () => {
		if (!user || !leadForConfirmation) return;

		const tracker = LeadAssignmentTracker.getInstance();
		const success = tracker.recordLeadAssignment(user.id);

		if (success) {
			try {
				await assignLead(leadForConfirmation._id.$oid, user.id);
				setLeads((prevLeads) =>
					prevLeads.map((lead) =>
						lead._id.$oid === leadForConfirmation._id.$oid ? { ...lead, assignedTo: user.id } : lead
					)
				);
				toast.success(t("dashboard.leadAssignedSuccess"));
			} catch {
				toast.error(t("dashboard.assignLeadError"));
			}
		} else {
			// Show error notification if assignment limit reached
			toast.error(t("dashboard.assignmentLimitReached"));
		}

		setConfirmationModalOpen(false);
		setLeadForConfirmation(null);
	};

	const handleCloseConfirmationModal = () => {
		setConfirmationModalOpen(false);
		setLeadForConfirmation(null);
	};

	const handleLeadCreated = (newLead: any) => {
		// Convert the backend lead to frontend format
		// Find the status in our statuses array
		const statusInfo = statuses.find((s) => s.id === newLead.statusId || s.name === newLead.status);
		const statusId = newLead.statusId || statusInfo?.id;
		const statusName = statusInfo?.name || newLead.status;
		const statusColor = statusInfo?.color;

		const mappedLead: Lead = {
			_id: { $oid: newLead.id },
			name: newLead.name,
			agentId: String(newLead.agentId),
			phoneNumber: newLead.phoneNumber,
			email: newLead.email,
			agentPhone: newLead.agentPhone || "",
			interested: newLead.interested || "",
			resumeAI: newLead.resumeAI,
			leadQuality: newLead.leadQuality as any as LeadQuality | undefined,
			status: ((): LeadStatus | undefined => {
				const raw = String(newLead.status || "").toLowerCase();
				const s = raw.replace(/\s+/g, "_");
				if (s === "novo") return "novo";
				if (
					s === "contato_inicial" ||
					s === "contato-inicial" ||
					s === "contatoinicial" ||
					s === "emandamento" ||
					s === "em_andamento"
				)
					return "contato_inicial";
				if (s === "qualificado") return "qualificado";
				if (s === "proposta") return "proposta";
				if (s === "negociacao" || s === "negociação") return "negociacao";
				if (s === "fechado" || s === "finalizado") return "fechado";
				if (s === "perdido") return "perdido";
				return "novo";
			})(),
			statusId: statusId,
			statusName: statusName,
			statusColor: statusColor,
			assignedTo: newLead.assignedTo || undefined,
			assignedToName: newLead.assignedToName || undefined,
			company: "",
			observations: "",
			observacoes: newLead.observacoes as any,
			campaignInfo: undefined,
			creationTime: { $date: newLead.createdAt || new Date().toISOString() },
		};

		// Add the new lead to the current leads list
		setLeads((prevLeads) => [mappedLead, ...prevLeads]);
	};

	const handleDeleteLead = (lead: Lead) => {
		setLeadToDelete(lead);
		setDeleteModalOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!leadToDelete) return;

		try {
			setIsDeleting(true);
			await deleteLead(leadToDelete._id.$oid);

			// Remove the lead from the current leads list
			setLeads((prevLeads) => prevLeads.filter((lead) => lead._id.$oid !== leadToDelete._id.$oid));

			// Close the lead detail modal if it's open for the deleted lead
			if (selectedLead && selectedLead._id.$oid === leadToDelete._id.$oid) {
				setSelectedLead(null);
			}

			toast.success(t("dashboard.leadDeletedSuccess"));
			setDeleteModalOpen(false);
			setLeadToDelete(null);
		} catch (err: any) {
			const errorMessage = err?.message || t("dashboard.deleteLeadError");
			toast.error(errorMessage);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleCloseDeleteModal = () => {
		if (isDeleting) return;
		setDeleteModalOpen(false);
		setLeadToDelete(null);
	};

	const handleGoToPlans = () => {
		navigate("/change-plan");
		setPlanInactiveModalOpen(false);
	};

	const handleClosePlanInactiveAlert = () => {
		setPlanInactiveAlertOpen(false);
	};

	const getLeadsAssignedThisHour = (): number => {
		if (!user || user.role !== "vendedor") return 0;
		const tracker = LeadAssignmentTracker.getInstance();
		return tracker.getAssignmentsInLastHour(user.id);
	};

	// Create kanban columns from dynamic statuses
	const kanbanColumns = statuses.reduce((acc, status) => {
		acc[status.id] = status.name;
		return acc;
	}, {} as Record<string, string>);

	const getFilteredLeads = () => {
		// Server-driven: return as-is for assigned tab; available tab handled separately
		return [...leads];
	};

	const getLeadsByStatus = (statusId: string) => {
		// Find the status info to get both ID and name
		const statusInfo = statuses.find((s) => s.id === statusId);
		const statusName = statusInfo?.name;

		return leads.filter(
			(lead) =>
				lead.statusId === statusId ||
				lead.status === statusId ||
				lead.status === statusName ||
				lead.statusName === statusName
		);
	};

	const handleLoadMoreAssignedStatus = async (statusId: string) => {
		if (assignedStatusLoading[statusId] || !assignedStatusHasMore[statusId]) return;
		setAssignedStatusLoading((prev) => ({ ...prev, [statusId]: true }));
		try {
			const { startDateISO, endDateISO } = (() => {
				const now = new Date();
				const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
				if (dateFilter === "today")
					return { startDateISO: startOfToday.toISOString(), endDateISO: undefined as string | undefined };
				if (dateFilter === "week")
					return {
						startDateISO: new Date(startOfToday.getTime() - 7 * 86400000).toISOString(),
						endDateISO: undefined,
					};
				if (dateFilter === "month")
					return {
						startDateISO: new Date(startOfToday.getTime() - 30 * 86400000).toISOString(),
						endDateISO: undefined,
					};
				if (dateFilter === "custom")
					return {
						startDateISO: customStartDate ? new Date(customStartDate).toISOString() : undefined,
						endDateISO: customEndDate ? new Date(customEndDate).toISOString() : undefined,
					};
				return { startDateISO: undefined, endDateISO: undefined };
			})();
			const currentCount = getLeadsByStatus(statusId).length;
			const backendStatusName = statuses.find((s) => s.id === statusId)?.name;
			const rs = await fetchAssignedLeadsPaged({
				skip: currentCount,
				take: 50,
				status: backendStatusName,
				quality: qualityFilter,
				sellerId: sellerFilter !== "all" ? sellerFilter : undefined,
				agentId: agentFilter !== "all" ? agentFilter : undefined,
				name: nameFilter || undefined,
				phone: phoneFilter || undefined,
				startDate: startDateISO,
				endDate: endDateISO,
			});
			const mapped = (rs || []).map((l) => {
				// Find the status in our statuses array
				const statusInfo = statuses.find((s) => s.id === l.statusId || s.name === l.status);
				const statusId = l.statusId || statusInfo?.id;
				const statusName = statusInfo?.name || l.status;
				const statusColor = statusInfo?.color;

				return {
					_id: { $oid: l.id },
					name: l.name,
					agentId: String(l.agentId),
					phoneNumber: l.phoneNumber,
					email: l.email,
					agentPhone: l.agentPhone || "",
					interested: l.interested || "",
					resumeAI: l.resumeAI,
					leadQuality: l.leadQuality as any as LeadQuality | undefined,
					status: ((): LeadStatus | undefined => {
						const raw = String(l.status || "").toLowerCase();
						const s = raw.replace(/\s+/g, "_");
						if (s === "novo") return "novo";
						if (
							s === "contato_inicial" ||
							s === "contato-inicial" ||
							s === "contatoinicial" ||
							s === "emandamento" ||
							s === "em_andamento"
						)
							return "contato_inicial";
						if (s === "qualificado") return "qualificado";
						if (s === "proposta") return "proposta";
						if (s === "negociacao" || s === "negociação") return "negociacao";
						if (s === "fechado" || s === "finalizado") return "fechado";
						if (s === "perdido") return "perdido";
						return undefined;
					})(),
					statusId: statusId,
					statusName: statusName,
					statusColor: statusColor,
					assignedTo: l.assignedTo || undefined,
					assignedToName: l.assignedToName || undefined,
					company: "",
					observations: "",
					observacoes: l.observacoes as any,
					campaignInfo: undefined,
					creationTime: { $date: l.creationTime || new Date().toISOString() },
				};
			}) as Lead[];
			setLeads((prev) => [...prev, ...mapped]);
			setAssignedStatusHasMore((prev) => ({ ...prev, [statusId]: mapped.length === 50 }));
		} finally {
			setAssignedStatusLoading((prev) => ({ ...prev, [statusId]: false }));
		}
	};

	const handleLoadMoreAssignedList = async () => {
		if (assignedGlobalLoading || !assignedGlobalHasMore) return;
		setAssignedGlobalLoading(true);
		try {
			const { startDateISO, endDateISO } = (() => {
				const now = new Date();
				const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
				if (dateFilter === "today")
					return { startDateISO: startOfToday.toISOString(), endDateISO: undefined as string | undefined };
				if (dateFilter === "week")
					return {
						startDateISO: new Date(startOfToday.getTime() - 7 * 86400000).toISOString(),
						endDateISO: undefined,
					};
				if (dateFilter === "month")
					return {
						startDateISO: new Date(startOfToday.getTime() - 30 * 86400000).toISOString(),
						endDateISO: undefined,
					};
				if (dateFilter === "custom")
					return {
						startDateISO: customStartDate ? new Date(customStartDate).toISOString() : undefined,
						endDateISO: customEndDate ? new Date(customEndDate).toISOString() : undefined,
					};
				return { startDateISO: undefined, endDateISO: undefined };
			})();
			const rs = await fetchAssignedLeadsPaged({
				skip: leads.length,
				take: 50,
				status: undefined,
				quality: qualityFilter,
				sellerId: sellerFilter !== "all" ? sellerFilter : undefined,
				agentId: agentFilter !== "all" ? agentFilter : undefined,
				name: nameFilter || undefined,
				phone: phoneFilter || undefined,
				startDate: startDateISO,
				endDate: endDateISO,
			});
			const mapped = (rs || []).map((l) => {
				// Find the status in our statuses array
				const statusInfo = statuses.find((s) => s.id === l.statusId || s.name === l.status);
				const statusId = l.statusId || statusInfo?.id;
				const statusName = statusInfo?.name || l.status;
				const statusColor = statusInfo?.color;

				return {
					_id: { $oid: l.id },
					name: l.name,
					agentId: String(l.agentId),
					phoneNumber: l.phoneNumber,
					email: l.email,
					agentPhone: l.agentPhone || "",
					interested: l.interested || "",
					resumeAI: l.resumeAI,
					leadQuality: l.leadQuality as any as LeadQuality | undefined,
					status: ((): LeadStatus | undefined => {
						const raw = String(l.status || "").toLowerCase();
						const s = raw.replace(/\s+/g, "_");
						if (s === "novo") return "novo";
						if (
							s === "contato_inicial" ||
							s === "contato-inicial" ||
							s === "contatoinicial" ||
							s === "emandamento" ||
							s === "em_andamento"
						)
							return "contato_inicial";
						if (s === "qualificado") return "qualificado";
						if (s === "proposta") return "proposta";
						if (s === "negociacao" || s === "negociação") return "negociacao";
						if (s === "fechado" || s === "finalizado") return "fechado";
						if (s === "perdido") return "perdido";
						return undefined;
					})(),
					statusId: statusId,
					statusName: statusName,
					statusColor: statusColor,
					assignedTo: l.assignedTo || undefined,
					assignedToName: l.assignedToName || undefined,
					company: "",
					observations: "",
					observacoes: l.observacoes as any,
					campaignInfo: undefined,
					creationTime: { $date: l.creationTime || new Date().toISOString() },
				};
			}) as Lead[];
			setLeads((prev) => [...prev, ...mapped]);
			setAssignedGlobalHasMore(mapped.length === 50);
		} finally {
			setAssignedGlobalLoading(false);
		}
	};

	const getColumnAccentColor = (statusId: string) => {
		const status = statuses.find((s) => s.id === statusId);
		return status?.color || "#4f46e5"; // Default Indigo 600
	};

	// Function to translate status name
	const translateStatus = (statusName: string | undefined, statusId: string | undefined): string => {
		if (!statusName && !statusId) return "-";

		const id = statusId || "";
		const name = statusName || "";

		// Try to translate by ID first (most reliable)
		if (id) {
			const translationKey = `dashboard.kanban.statusLabels.${id}`;
			const translated = t(translationKey);

			// If translation exists and is different from the key, return it
			if (translated !== translationKey) {
				return translated;
			}
		}

		// Fallback: try to translate by normalized name
		if (name) {
			const normalizedName = name.toLowerCase().replace(/\s+/g, "_");
			const nameTranslationKey = `dashboard.kanban.statusLabels.${normalizedName}`;
			const nameTranslated = t(nameTranslationKey);

			if (nameTranslated !== nameTranslationKey) {
				return nameTranslated;
			}
		}

		// If no translation found, return original name
		return name || id || "-";
	};

	// Skeleton component for kanban loading
	const KanbanSkeleton = () => (
		<Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 2 }}>
			{[1, 2, 3, 4, 5].map((index) => (
				<Box
					key={index}
					sx={{
						minWidth: 350,
						height: 500,
						borderRadius: 2,
						border: "1px solid",
						borderColor: "divider",
						p: 2,
						display: "flex",
						flexDirection: "column",
						gap: 2,
					}}
				>
					{/* Header skeleton */}
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
						<Skeleton variant="text" width="60%" height={32} />
						<Skeleton variant="circular" width={28} height={28} />
					</Box>

					{/* Content skeleton */}
					<Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
						{[1, 2, 3].map((cardIndex) => (
							<Box
								key={cardIndex}
								sx={{
									p: 2,
									borderRadius: 2,
									border: "1px solid",
									borderColor: "divider",
									display: "flex",
									flexDirection: "column",
									gap: 1,
								}}
							>
								<Skeleton variant="text" width="80%" height={20} />
								<Skeleton variant="text" width="60%" height={16} />
								<Skeleton variant="text" width="40%" height={14} />
								<Box sx={{ display: "flex", gap: 1, mt: 1 }}>
									<Skeleton variant="rectangular" width="50%" height={32} sx={{ borderRadius: 1 }} />
									<Skeleton variant="rectangular" width="50%" height={32} sx={{ borderRadius: 1 }} />
								</Box>
							</Box>
						))}
					</Box>
				</Box>
			))}
		</Box>
	);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const hasInitializedViewMode = useRef(false);
	useEffect(() => {
		if (!hasInitializedViewMode.current) {
			setViewMode(isMobile ? "list" : "kanban");
			hasInitializedViewMode.current = true;
		}
	}, [isMobile]);

	// Handle URL-based lead opening
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const queryLeadId = searchParams.get("leadId") || leadId;

		if (queryLeadId && leads.length > 0) {
			const lead = leads.find((l) => l._id.$oid === queryLeadId);
			if (lead) {
				setSelectedLead(lead);
				// Clear the URL parameter after opening the modal
				navigate("/dashboard", { replace: true });
			}
		}
	}, [leadId, location.search, leads, navigate]);

	// Listen for real-time lead updates from notifications
	useEffect(() => {
		const handleLeadUpdate = (leadId: string, updateType: "assigned" | "transferred") => {
			console.log(`🚀 DashboardPage - Lead ${leadId} was ${updateType}, refreshing leads...`);

			// Refresh leads data
			let isMounted = true;
			const refreshLeads = async () => {
				try {
					let backendLeads;
					const { startDateISO, endDateISO } = (() => {
						const now = new Date();
						const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
						if (dateFilter === "today")
							return {
								startDateISO: startOfToday.toISOString(),
								endDateISO: undefined as string | undefined,
							};
						if (dateFilter === "week")
							return {
								startDateISO: new Date(startOfToday.getTime() - 7 * 86400000).toISOString(),
								endDateISO: undefined,
							};
						if (dateFilter === "month")
							return {
								startDateISO: new Date(startOfToday.getTime() - 30 * 86400000).toISOString(),
								endDateISO: undefined,
							};
						return { startDateISO: undefined, endDateISO: undefined };
					})();
					const rs = await fetchAssignedLeadsPaged({
						skip: 0,
						take: 50, // Refresh with a reasonable number of leads
						status: undefined,
						quality: qualityFilter,
						sellerId: sellerFilter !== "all" ? sellerFilter : undefined,
						agentId: agentFilter !== "all" ? agentFilter : undefined,
						name: nameFilter || undefined,
						phone: phoneFilter || undefined,
						startDate: startDateISO,
						endDate: endDateISO,
					});
					backendLeads = rs;

					if (isMounted && backendLeads) {
						const mapped = backendLeads.map((l: any) => ({
							...l,
							_id: { $oid: l.id || l._id?.$oid },
							creationTime: { $date: l.creationTime || new Date().toISOString() },
						})) as Lead[];
						setLeads(mapped);
					}
				} catch (error) {
					console.error("Failed to refresh leads:", error);
				}
			};

			refreshLeads();
			return () => {
				isMounted = false;
			};
		};

		console.log("📝 DashboardPage - Registering lead update callback");
		onLeadUpdate(handleLeadUpdate);

		return () => {
			offLeadUpdate(handleLeadUpdate);
		};
	}, [
		onLeadUpdate,
		offLeadUpdate,
		nameFilter,
		phoneFilter,
		dateFilter,
		customStartDate,
		customEndDate,
		sellerFilter,
		qualityFilter,
	]);

	const handleOpenLeadDetail = (leadId: string) => {
		const lead = leads.find((l) => l._id.$oid === leadId);
		if (lead) {
			setSelectedLead(lead);
		}
	};

	const handleOpenConversation = (conversationId: string) => {
		console.log("🎯 DashboardPage - Opening conversation from notification:", conversationId);

		// Set localStorage flag to indicate we're coming from a notification
		localStorage.setItem("skipAgentModal", "true");
		console.log("🎯 DashboardPage - Set skipAgentModal flag in localStorage");

		// Navigate to conversations page with the conversation ID as a URL parameter
		navigate(`/conversations?conversationId=${encodeURIComponent(conversationId)}`);
	};

	const getAssignedSellerName = (lead: Lead): string => {
		const assignedUser = users.find((u) => u.id === lead.assignedTo);
		if (assignedUser) {
			return assignedUser.name;
		}
		const assignedAgentUser = agentUsers.find((au) => au.userId === lead.assignedTo);
		if (assignedAgentUser) {
			return assignedAgentUser.userName || assignedAgentUser.userId;
		}
		return lead.assignedToName || lead.assignedTo || "-";
	};

	const commonFilterStyles = {
		"& .MuiInputBase-root": {
			fontSize: isMobile ? "0.875rem" : "1rem",
			"& fieldset": {
				borderColor: theme.palette.mode === "dark" ? "rgb(3, 3, 3)" : "rgba(0, 0, 0, 0.23)",
			},
			"&:hover fieldset": {
				borderColor: theme.palette.mode === "dark" ? "rgb(3, 3, 3)" : "rgba(0, 0, 0, 0.87)",
			},
			"&.Mui-focused fieldset": {
				borderColor: theme.palette.primary.main,
			},
		},
	};

	return (
		<Box sx={{ flexGrow: 1 }}>
			<AppBar position="static">
				<Toolbar
					sx={{
						flexDirection: isMobile ? "column" : "row",
						alignItems: isMobile ? "stretch" : "center",
						py: isMobile ? 1 : 0,
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							flexGrow: 1,
							justifyContent: isMobile ? "center" : "flex-start",
							mb: isMobile ? 1 : 0,
						}}
					>
						{(isAllowedDomain() || dynamicLogo) && (
							<>
								{logoLoading ? (
									<CircularProgress
										size={isMobile ? 20 : 24}
										sx={{ mr: isMobile ? "8px" : "16px" }}
									/>
								) : (
									<img
										src={dynamicLogo || "/logo-branco.svg"}
										alt="Momentum Logo"
										style={{
											width: isMobile ? "120px" : "130px",
											marginRight: isMobile ? "8px" : "16px",
										}}
										onError={() => setDynamicLogo(null)} // Fallback para logo padrão em caso de erro
									/>
								)}
							</>
						)}
					</Box>

					{!isMobile && (
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, mr: 2 }}>
							<Typography variant="body1">{user?.name}</Typography>

							{/* Exibir dias restantes de teste */}
							{trialData && trialData.isTrialActive && canShowAlert("showTrialAlerts") && (
								<Chip
									icon={<TimeIcon />}
									label={`${trialData.daysRemaining} ${t("dashboard.daysRemaining")}`}
									color="warning"
									variant="outlined"
									size="small"
									sx={{
										fontWeight: 600,
										"& .MuiChip-icon": {
											color: "warning.main",
										},
									}}
								/>
							)}
						</Box>
					)}

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: isMobile ? "space-between" : "flex-end",
							width: isMobile ? "100%" : "auto",
						}}
					>
						<IconButton
							size="large"
							color="inherit"
							onClick={toggleMode}
							title={mode === "dark" ? t("dashboard.toggleLightMode") : t("dashboard.toggleDarkMode")}
							sx={{ mr: 1 }}
						>
							{mode === "dark" ? <LightMode /> : <DarkMode />}
						</IconButton>
						<LanguageSelector />
						{isMobile && (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
									{user?.name}
								</Typography>

								{/* Exibir dias restantes de teste no mobile */}
								{trialData && trialData.isTrialActive && canShowAlert("showTrialAlerts") && (
									<Chip
										icon={<TimeIcon />}
										label={`${trialData.daysRemaining}${t("dashboard.daysRemainingShort")}`}
										color="warning"
										variant="outlined"
										size="small"
										sx={{
											fontWeight: 600,
											fontSize: "0.7rem",
											height: "24px",
											"& .MuiChip-icon": {
												color: "warning.main",
												fontSize: "0.8rem",
											},
										}}
									/>
								)}
							</Box>
						)}
						<NotificationCenter
							onOpenLeadDetail={handleOpenLeadDetail}
							onOpenConversation={handleOpenConversation}
						/>
						{canViewMenu("canViewConversations") && (
							<IconButton
								component={Link}
								to="/conversations"
								size="large"
								title={t("conversations.title")}
							>
								<ChatIcon sx={{ color: "#00f5ff" }} />
							</IconButton>
						)}
						{isAdminLike(user?.role || "") && (
							<IconButton component={Link} to="/admin" size="large" title={t("admin.title")}>
								<AdminPanelSettings sx={{ color: "#35e87c" }} />
							</IconButton>
						)}
						<IconButton size="large" edge="end" color="inherit" onClick={handleMenuOpen}>
							<AccountCircle />
						</IconButton>
					</Box>

					<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
						<MenuItem onClick={handleChangePassword}>
							<Lock sx={{ mr: 1 }} />
							{t("common.changePassword")}
						</MenuItem>
						<MenuItem onClick={handleLogout}>
							<Logout sx={{ mr: 1 }} />
							{t("common.logout")}
						</MenuItem>
					</Menu>
				</Toolbar>
			</AppBar>

			{/* Alerta de plano inativo */}
			{planInactiveAlertOpen &&
				user?.isPlanActive === false &&
				canShowAlert("showPlanInactiveAlerts") &&
				!shouldHidePaymentElements && (
					<Box sx={{ px: 2, pt: 2 }}>
						<PlanInactiveAlert onClose={handleClosePlanInactiveAlert} onGoToPlans={handleGoToPlans} />
					</Box>
				)}

			{/* Change Password Modal */}
			<Dialog open={changePwdOpen} onClose={handleCloseChangePwd} fullWidth maxWidth="xs">
				<DialogTitle>{t("dashboard.changePassword")}</DialogTitle>
				<Box component="form" onSubmit={handleSubmitChangePwd}>
					<DialogContent>
						{!!changePwdError && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{changePwdError}
							</Alert>
						)}
						{!!changePwdSuccess && (
							<Alert severity="success" sx={{ mb: 2 }}>
								{changePwdSuccess}
							</Alert>
						)}
						{!!passwordValidationError && (
							<Alert severity="warning" sx={{ mb: 2 }}>
								{passwordValidationError}
							</Alert>
						)}
						<TextField
							label={t("dashboard.currentPassword")}
							type="password"
							fullWidth
							margin="normal"
							value={currentPassword}
							onChange={(e) => setCurrentPassword(e.target.value)}
							autoFocus
							required
							error={!!changePwdError && changePwdError.includes(t("dashboard.incorrectPassword"))}
						/>
						<TextField
							label={t("dashboard.newPassword")}
							type="password"
							fullWidth
							margin="normal"
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							required
							error={newPassword.length > 0 && newPassword.length < 6}
							helperText={
								newPassword.length > 0 && newPassword.length < 6 ? t("dashboard.passwordMinLength") : ""
							}
						/>
						<TextField
							label={t("dashboard.confirmNewPassword")}
							type="password"
							fullWidth
							margin="normal"
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							required
							error={!!passwordValidationError}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCloseChangePwd} disabled={changePwdLoading}>
							{t("common.cancel")}
						</Button>
						<Button
							type="submit"
							variant="contained"
							disabled={changePwdLoading || !isFormValid}
							sx={{
								color: theme.palette.mode === "dark" ? "#000" : undefined,
								"&:hover": {
									backgroundColor: theme.palette.mode === "dark" ? "#0284c7" : "#0284c7",
								},
								"&:disabled": {
									backgroundColor: theme.palette.action.disabledBackground,
									color: theme.palette.action.disabled,
								},
							}}
						>
							{changePwdLoading ? (
								<CircularProgress size={20} sx={{ color: "white" }} />
							) : (
								t("common.save")
							)}
						</Button>
					</DialogActions>
				</Box>
			</Dialog>

			<Container maxWidth={false} sx={{ mt: 2, px: isMobile ? 1 : 2 }}>
				{canShowAlert("showWelcomeModals") && !shouldHidePaymentElements && (
					<WelcomeAssistantModal open={welcomeOpen} onClose={() => setWelcomeOpen(false)} />
				)}
				{/* <Paper sx={{ mb: 2 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant={isMobile ? "fullWidth" : "standard"}
            sx={{
              '& .MuiTab-root': {
                fontSize: isMobile ? '0.875rem' : '1rem',
                minHeight: isMobile ? '48px' : 'auto',
              }
            }}
          >
            <Tab label={
              user?.role === 'admin' ? 'Todos os Leads' :
                user?.role === 'gerente' ? 'Leads da Equipe' : 'Meus Leads'
            } />
          </Tabs>
        </Paper> */}

				{currentTab === 0 && (
					<Box
						sx={{
							display: "flex",
							flexDirection: isMobile ? "column" : "row",
							justifyContent: "space-between",
							alignItems: isMobile ? "stretch" : "center",
							mb: 2,
							gap: isMobile ? 2 : 0,
							// Para telas >= 1280px, garantir que os elementos fiquem em linha única sem sobreposição
							"@media (min-width: 1280px)": {
								flexDirection: "row",
								justifyContent: "space-between",
								alignItems: "center",
								gap: 3, // Aumentar gap para melhor espaçamento entre chip e botões
								minHeight: "72px",
								// Garantir que o container tenha largura total disponível
								width: "100%",
								maxWidth: "100%",
							},
						}}
					>
						{/* Filtros - Layout responsivo */}
						<Box
							sx={{
								display: "flex",
								flexDirection: isMobile ? "column" : "row",
								gap: isMobile ? 1 : 2,
								alignItems: isMobile ? "stretch" : "center",
								width: isMobile ? "100%" : "auto",
								// Scroll horizontal apenas para telas maiores quando necessário
								overflowX: isMobile ? "visible" : "auto",
								pb: isMobile ? 0 : 1,
								minHeight: isMobile ? "auto" : "72px",
								pt: isMobile ? 0 : 1,
								// Para telas >= 1280px, usar flexbox inteligente para detectar espaço
								"@media (min-width: 1280px)": {
									flexDirection: "row",
									alignItems: "center",
									overflowX: "visible",
									flex: "1 1 auto",
									minWidth: 0,
									gap: 2, // Manter gap original
									// Usar min-content para permitir que os elementos voltem ao tamanho natural quando há espaço
									width: "min-content",
									maxWidth: "none",
								},
								// Para telas menores (1280px-1600px), aplicar limitações apenas quando necessário
								"@media (min-width: 1280px) and (max-width: 1599px)": {
									width: "auto",
									maxWidth: "calc(100% - 320px)", // Reservar espaço para os botões apenas quando necessário
									gap: 1, // Reduzir gap para economizar espaço
								},
								"&::-webkit-scrollbar": {
									height: "6px",
									display: isMobile ? "none" : "block",
								},
								"&::-webkit-scrollbar-track": {
									background: theme.palette.mode === "dark" ? "#313131" : "#f1f1f1",
									borderRadius: "3px",
								},
								"&::-webkit-scrollbar-thumb": {
									background: theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
									borderRadius: "3px",
									"&:hover": {
										background: theme.palette.mode === "dark" ? "#666" : "#a8a8a8",
									},
								},
							}}
						>
							<FormControl
								size="small"
								sx={{
									minWidth: isMobile ? "100%" : 180,
									flexShrink: isMobile ? 1 : 0,
									...commonFilterStyles,
									// Para telas >= 1280px, voltar ao tamanho natural quando há espaço
									"@media (min-width: 1280px)": {
										minWidth: 180,
										flexShrink: 0,
										width: "auto",
									},
									// Para telas menores (1280px-1600px), aplicar otimizações se necessário
									"@media (min-width: 1280px) and (max-width: 1599px)": {
										minWidth: 140,
										flexShrink: 1,
										maxWidth: 160,
									},
								}}
							>
								<InputLabel>{t("dashboard.agent")}</InputLabel>
								<Select
									value={agentFilter}
									label={t("dashboard.agent")}
									onChange={(e) => {
										setAgentFilter(e.target.value);
										// Reset seller filter when agent changes to avoid inconsistencies
										setSellerFilter("all");
									}}
								>
									<MenuItem value="all">{t("dashboard.all")}</MenuItem>
									{agents.map((agent) => (
										<MenuItem key={agent.id} value={agent.id}>
											{agent.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl
								size="small"
								sx={{
									minWidth: isMobile ? "100%" : 180,
									flexShrink: isMobile ? 1 : 0,
									...commonFilterStyles,
									// Para telas >= 1280px, voltar ao tamanho natural quando há espaço
									"@media (min-width: 1280px)": {
										minWidth: 180,
										flexShrink: 0,
										width: "auto",
									},
									// Para telas menores (1280px-1600px), aplicar otimizações se necessário
									"@media (min-width: 1280px) and (max-width: 1599px)": {
										minWidth: 140,
										flexShrink: 1,
										maxWidth: 160,
									},
								}}
							>
								<InputLabel>{t("dashboard.quality")}</InputLabel>
								<Select
									value={qualityFilter}
									label={t("dashboard.quality")}
									onChange={(e) => setQualityFilter(e.target.value as LeadQuality | "all")}
								>
									<MenuItem value="all">{t("dashboard.allQualities")}</MenuItem>
									<MenuItem value="Quente">{t("dashboard.hot")}</MenuItem>
									<MenuItem value="Neutro">{t("dashboard.neutral")}</MenuItem>
									<MenuItem value="Frio">{t("dashboard.cold")}</MenuItem>
								</Select>
							</FormControl>

							<FormControl
								size="small"
								sx={{
									minWidth: isMobile ? "100%" : 150,
									flexShrink: isMobile ? 1 : 0,
									...commonFilterStyles,
									// Para telas >= 1280px, reduzir significativamente o tamanho para evitar sobreposição
									"@media (min-width: 1280px)": {
										minWidth: 120,
										flexShrink: 1, // Permite encolher se necessário
										maxWidth: 140,
									},
								}}
							>
								<InputLabel>{t("dashboard.date")}</InputLabel>
								<Select
									value={dateFilter}
									label={t("dashboard.date")}
									onChange={(e) => {
										const v = e.target.value as "all" | "today" | "week" | "month" | "custom";
										setDateFilter(v);
										if (v === "custom") setCustomDateOpen(true);
									}}
								>
									<MenuItem value="all">{t("dashboard.allDates")}</MenuItem>
									<MenuItem value="today">{t("dashboard.today")}</MenuItem>
									<MenuItem value="week">{t("dashboard.lastWeek")}</MenuItem>
									<MenuItem value="month">{t("dashboard.lastMonth")}</MenuItem>
									<MenuItem value="custom" onClick={() => setCustomDateOpen(true)}>
										{t("dashboard.custom")}
									</MenuItem>
								</Select>
							</FormControl>

							{(isAdminLike(user?.role || "") || isManagerRole(user?.role || "")) && (
								<FormControl
									size="small"
									sx={{
										minWidth: isMobile ? "100%" : 180,
										flexShrink: isMobile ? 1 : 0,
										...commonFilterStyles,
										// Para telas >= 1280px, voltar ao tamanho natural quando há espaço
										"@media (min-width: 1280px)": {
											minWidth: 180,
											flexShrink: 0,
											width: "auto",
										},
										// Para telas menores (1280px-1600px), aplicar otimizações se necessário
										"@media (min-width: 1280px) and (max-width: 1599px)": {
											minWidth: 140,
											flexShrink: 1,
											maxWidth: 160,
										},
									}}
								>
									<InputLabel>{t("dashboard.seller")}</InputLabel>
									<Select
										value={sellerFilter}
										label={t("dashboard.seller")}
										onChange={(e) => setSellerFilter(e.target.value)}
									>
										<MenuItem value="all">{t("dashboard.all")}</MenuItem>
										{agentUsers
											.filter(
												(au) =>
													au.isActive &&
													// Filter by selected agent if one is selected
													(agentFilter === "all" || au.agentId === agentFilter) &&
													// Apply role-based permissions
													(isAdminLike(user?.role || "") ||
														(isManagerRole(user?.role || "") &&
															user?.id &&
															users.find((u) => u.id === au.userId)?.managerId ===
																user?.id))
											)
											// Remove duplicates by userId - keep only unique users
											.reduce((unique, agentUser) => {
												if (!unique.find((au) => au.userId === agentUser.userId)) {
													unique.push(agentUser);
												}
												return unique;
											}, [] as AgentUserRS[])
											.map((agentUser) => (
												<MenuItem key={agentUser.userId} value={agentUser.userId}>
													{agentUser.userName || agentUser.userId}
												</MenuItem>
											))}
									</Select>
								</FormControl>
							)}

							<TextField
								size="small"
								label={t("dashboard.leadName")}
								value={nameFilter}
								onChange={(e) => setNameFilter(e.target.value)}
								placeholder={t("dashboard.searchByName")}
								sx={{
									minWidth: isMobile ? "100%" : 180,
									flexShrink: isMobile ? 1 : 0,
									...commonFilterStyles,
									// Para telas >= 1280px, voltar ao tamanho natural quando há espaço
									"@media (min-width: 1280px)": {
										minWidth: 180,
										flexShrink: 0,
										width: "auto",
									},
									// Para telas menores (1280px-1600px), aplicar otimizações se necessário
									"@media (min-width: 1280px) and (max-width: 1599px)": {
										minWidth: 140,
										flexShrink: 1,
										maxWidth: 160,
									},
								}}
							/>

							<TextField
								size="small"
								label={t("dashboard.phone")}
								value={phoneFilter}
								onChange={(e) => setPhoneFilter(e.target.value)}
								placeholder={t("dashboard.searchByPhone")}
								sx={{
									minWidth: isMobile ? "100%" : 150,
									flexShrink: isMobile ? 1 : 0,
									...commonFilterStyles,
									// Para telas >= 1280px, manter tamanho original quando há espaço
									"@media (min-width: 1280px)": {
										minWidth: 150,
										flexShrink: 0,
									},
									// Para telas menores (1280px-1600px), aplicar otimizações se necessário
									"@media (min-width: 1280px) and (max-width: 1599px)": {
										minWidth: 130,
										flexShrink: 1,
										maxWidth: 150,
									},
								}}
							/>

							<Chip
								icon={<FilterList />}
								label={`${getFilteredLeads().length} ${t("dashboard.leadsCount")}`}
								color="primary"
								variant="outlined"
								sx={{
									fontSize: isMobile ? "0.8rem" : "0.875rem",
									height: isMobile ? "32px" : "auto",
									flexShrink: isMobile ? 1 : 0,
									// Para telas >= 1280px, manter tamanho original
									"@media (min-width: 1280px)": {
										flexShrink: 0,
										fontSize: "0.875rem",
									},
								}}
							/>
						</Box>

						{/* Botões - Layout com grupos que se movem juntos */}
						<Box
							sx={{
								display: "flex",
								gap: 1,
								flexDirection: "row",
								width: isMobile ? "100%" : "auto",
								justifyContent: isMobile ? "center" : "flex-end",
								flexWrap: "wrap",
								alignItems: "center",
								minWidth: isMobile ? "auto" : "200px",
								// Para telas >= 1280px, manter comportamento original quando há espaço
								"@media (min-width: 1280px)": {
									flexWrap: "nowrap",
									flexShrink: 0,
									minWidth: "auto",
									justifyContent: "flex-end",
									width: "auto",
								},
								// Para telas menores (1280px-1600px), aplicar limitações se necessário
								"@media (min-width: 1280px) and (max-width: 1599px)": {
									minWidth: "280px",
									maxWidth: "300px",
								},
							}}
						>
							{/* Grupo 1: Botões de visualização */}
							<Box
								sx={{
									display: "flex",
									gap: 1,
									alignItems: "center",
									flexShrink: 0,
									// Para telas >= 1280px, garantir que os botões mantenham tamanho consistente
									"@media (min-width: 1280px)": {
										flexShrink: 0,
									},
								}}
							>
								<IconButton
									onClick={() => setViewMode("kanban")}
									sx={{
										border: "1px solid",
										borderColor:
											viewMode === "kanban"
												? "primary.main"
												: theme.palette.mode === "dark"
												? "#fff"
												: "rgba(0, 0, 0, 0.23)",
										backgroundColor:
											viewMode === "kanban"
												? theme.palette.mode === "dark"
													? "primary.main"
													: "#1a1a1a"
												: "transparent",
										color:
											viewMode === "kanban"
												? theme.palette.mode === "dark"
													? "#000"
													: "#fff"
												: undefined,
										minWidth: isMobile ? "48px" : "40px",
										minHeight: isMobile ? "48px" : "40px",
										"&:hover": {
											borderColor: "rgba(0, 0, 0, 0.87)",
											backgroundColor:
												viewMode === "kanban"
													? theme.palette.mode === "dark"
														? "primary.dark"
														: "#34495e"
													: "rgba(0, 0, 0, 0.04)",
										},
									}}
									title={t("dashboard.kanbanView")}
								>
									<ViewKanban sx={{ fontSize: isMobile ? "20px" : "18px" }} />
								</IconButton>
								<IconButton
									onClick={() => setViewMode("list")}
									sx={{
										border: "1px solid",
										borderColor:
											viewMode === "list"
												? "primary.main"
												: theme.palette.mode === "dark"
												? "#fff"
												: "rgba(0, 0, 0, 0.23)",
										backgroundColor:
											viewMode === "list"
												? theme.palette.mode === "dark"
													? "primary.main"
													: "#1a1a1a"
												: "transparent",
										color:
											viewMode === "list"
												? theme.palette.mode === "dark"
													? "#000"
													: "#fff"
												: undefined,
										minWidth: isMobile ? "48px" : "40px",
										minHeight: isMobile ? "48px" : "40px",
										"&:hover": {
											borderColor: "rgba(0, 0, 0, 0.87)",
											backgroundColor:
												viewMode === "list"
													? theme.palette.mode === "dark"
														? "primary.dark"
														: "#34495e"
													: "rgba(0, 0, 0, 0.04)",
										},
									}}
									title={t("dashboard.listView")}
								>
									<ViewList sx={{ fontSize: isMobile ? "20px" : "18px" }} />
								</IconButton>
								<IconButton
									onClick={() => {
										setQualityFilter("all");
										setDateFilter("all");
										setSellerFilter("all");
										setAgentFilter("all");
										setNameFilter("");
										setPhoneFilter("");
										setCustomStartDate("");
										setCustomEndDate("");
									}}
									sx={{
										border: "1px solid rgba(0, 0, 0, 0.23)",
										minWidth: isMobile ? "48px" : "40px",
										minHeight: isMobile ? "48px" : "40px",
										"&:hover": {
											borderColor: "rgba(0, 0, 0, 0.87)",
											backgroundColor: "rgba(0, 0, 0, 0.04)",
										},
									}}
									title={t("dashboard.clearFilters")}
								>
									<Clear sx={{ fontSize: isMobile ? "20px" : "18px" }} />
								</IconButton>
							</Box>

							{/* Grupo 2: Botões de ação (sempre juntos) */}
							<Box
								sx={{
									display: "flex",
									gap: 1,
									alignItems: "center",
									flexShrink: 0,
									// Para telas >= 1280px, garantir que os botões mantenham tamanho consistente
									"@media (min-width: 1280px)": {
										flexShrink: 0,
									},
								}}
							>
								<Button
									variant="contained"
									startIcon={<Add />}
									onClick={() => setCreateLeadModalOpen(true)}
									sx={{
										backgroundColor: "#35e87c",
										color: "#1a1a1a",
										minWidth: isMobile ? "auto" : "120px",
										minHeight: isMobile ? "48px" : "40px",
										"&:hover": {
											backgroundColor: "#1b5e20",
											color: "#fff",
										},
										textTransform: "none",
										fontSize: isMobile ? "0.75rem" : "0.875rem",
										"@media (max-width: 1320px)": {
											minWidth: "80px",
										},
									}}
									title={t("dashboard.createNewLead")}
								>
									<Box
										component="span"
										sx={{
											"@media (max-width: 1320px)": {
												"&::after": {
													content: '""',
												},
											},
											"@media (min-width: 1320px)": {
												"&::after": {
													content: '" Lead"',
												},
											},
										}}
									>
										{t("dashboard.new")}
									</Box>
								</Button>
								<IconButton
									onClick={handleExportToExcel}
									sx={{
										backgroundColor: "#35e87c",
										color: theme.palette.mode === "dark" ? "#000" : undefined,
										minWidth: isMobile ? "48px" : "40px",
										minHeight: isMobile ? "48px" : "40px",
										"&:hover": {
											color: "white",
											backgroundColor: "#1565c0",
										},
									}}
									title={t("dashboard.exportExcel")}
								>
									<FileDownload sx={{ fontSize: isMobile ? "20px" : "18px" }} />
								</IconButton>
							</Box>
						</Box>
					</Box>
				)}

				{currentTab === 0 && viewMode === "kanban" && (
					<DndContext
						collisionDetection={closestCorners}
						onDragStart={handleDragStart}
						onDragEnd={handleDragEnd}
						sensors={sensors}
						autoScroll={true}
					>
						<Box
							sx={{
								position: "relative",
								overflow: "hidden",
								pb: 2,
								borderRadius: 2,
								background:
									theme.palette.mode === "dark"
										? "transparent"
										: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
								"&::before": {
									content: '""',
									position: "absolute",
									top: 0,
									left: 0,
									right: 0,
									height: "100%",
									background:
										"linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.02) 50%, transparent 100%)",
									pointerEvents: "none",
									opacity: isMobile ? 0 : 0.5,
								},
							}}
						>
							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									gap: isMobile ? 1 : 2,
									overflowX: "auto",
									overflowY: "hidden",
									pb: 2,
									minHeight: isMobile ? "auto" : 600,
									scrollBehavior: "smooth",
									"&::-webkit-scrollbar": {
										height: "12px",
									},
									"&::-webkit-scrollbar-track": {
										background:
											theme.palette.mode === "dark"
												? "#313131"
												: "linear-gradient(90deg, #f1f1f1 0%, #e8e8e8 100%)",
										borderRadius: "6px",
										margin: "0 20px",
									},
									"&::-webkit-scrollbar-thumb": {
										background:
											theme.palette.gradient?.primary ||
											"linear-gradient(90deg, #1a1a1a 0%, #1a1a1a 100%)",
										borderRadius: "6px",
										boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
										"&:hover": {
											background: "linear-gradient(90deg, #1a1a1a 0%, #1a1a1a 100%)",
											boxShadow: "0 3px 6px rgba(0,0,0,0.15)",
										},
									},
									// Subtle fade indicators for scroll content
									"&::before": {
										content: '""',
										position: "absolute",
										left: 0,
										top: 0,
										bottom: 20,
										width: "15px",
										zIndex: 10,
										pointerEvents: "none",
										opacity: isMobile ? 0 : 1,
									},
									"&::after": {
										content: '""',
										position: "absolute",
										right: 0,
										top: 0,
										bottom: 20,
										width: "30px",
										zIndex: 10,
										pointerEvents: "none",
										opacity: isMobile ? 0 : 1,
									},
								}}
								data-scroll-container
							>
								{statusesLoading ? (
									<KanbanSkeleton />
								) : (
									statuses.map((status) => {
										const columnLeads = getLeadsByStatus(status.id);
										const hasMoreForStatus = assignedStatusHasMore[status.id];
										return (
											<KanbanColumn
												key={status.id}
												id={status.id}
												title={status.name}
												leads={columnLeads}
												users={users}
												agentUsers={agentUsers}
												onViewDetails={setSelectedLead}
												onAssign={
													permissions.canAssignLeads
														? handleOpenAssignModal
														: isSalesRole(user?.role || "")
														? handleAssignLeadFromModal
														: undefined
												}
												onTransfer={
													permissions.canAssignLeads ? handleOpenTransferModal : undefined
												}
												isMobile={isMobile}
												onLoadMore={
													hasMoreForStatus
														? () => handleLoadMoreAssignedStatus(status.id)
														: undefined
												}
												canLoadMore={hasMoreForStatus}
												loadingMore={assignedStatusLoading[status.id]}
												statusColor={status.color}
											/>
										);
									})
								)}
							</Box>

							{/* Scroll hint indicators */}
							{!isMobile && (
								<>
									{/* Arrow da esquerda */}
									<Box
										sx={{
											position: "absolute",
											left: 10,
											top: "50%",
											transform: "translateY(-50%)",
											zIndex: 11,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											width: 40,
											height: 40,
											borderRadius: "50%",
											background: "rgba(44, 62, 80, 0.7)",
											color: "white",
											boxShadow: "0 2px 8px rgba(44, 62, 80, 0.2)",
											cursor: "pointer",
											"&:hover": {
												background: "rgba(44, 62, 80, 0.9)",
												transform: "translateY(-50%) scale(1.05)",
												boxShadow: "0 4px 12px rgba(44, 62, 80, 0.3)",
											},
											transition: "all 0.2s ease",
										}}
										onClick={(e) => {
											const container =
												e.currentTarget.parentElement?.querySelector("[data-scroll-container]");
											if (container) {
												container.scrollBy({ left: -300, behavior: "smooth" });
											}
										}}
										title={t("dashboard.scrollLeft")}
									>
										<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
											<path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
										</svg>
									</Box>

									{/* Arrow da direita */}
									<Box
										sx={{
											position: "absolute",
											right: 10,
											top: "50%",
											transform: "translateY(-50%)",
											zIndex: 11,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											width: 40,
											height: 40,
											borderRadius: "50%",
											background: "rgba(44, 62, 80, 0.7)",
											color: "white",
											boxShadow: "0 2px 8px rgba(44, 62, 80, 0.2)",
											cursor: "pointer",
											"&:hover": {
												background: "rgba(44, 62, 80, 0.9)",
												transform: "translateY(-50%) scale(1.05)",
												boxShadow: "0 4px 12px rgba(44, 62, 80, 0.3)",
											},
											transition: "all 0.2s ease",
										}}
										onClick={(e) => {
											const container =
												e.currentTarget.parentElement?.querySelector("[data-scroll-container]");
											if (container) {
												container.scrollBy({ left: 300, behavior: "smooth" });
											}
										}}
										title={t("dashboard.scrollRight")}
									>
										<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
											<path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
										</svg>
									</Box>
								</>
							)}
						</Box>
						<DragOverlay>
							{activeLead && (
								<Box
									sx={{
										transform: "rotate(2deg) scale(1.03)",
										opacity: 0.9,
										cursor: "grabbing",
										pointerEvents: "none",
										filter: "drop-shadow(0 8px 25px rgba(0,0,0,0.15))",
									}}
								>
									<LeadCard
										lead={activeLead}
										onViewDetails={() => {}}
										users={users}
										agentUsers={agentUsers}
										isDragging={true}
										isMobile={isMobile}
									/>
								</Box>
							)}
						</DragOverlay>
					</DndContext>
				)}

				{currentTab === 0 && viewMode === "list" && (
					<Box sx={{ mt: 2 }}>
						<TableContainer component={Paper}>
							<Table size={isMobile ? "small" : "medium"}>
								<TableHead>
									<TableRow>
										<TableCell>{t("dashboard.name")}</TableCell>
										<TableCell>{t("dashboard.quality")}</TableCell>
										<TableCell>{t("dashboard.status")}</TableCell>
										<TableCell>{t("dashboard.seller")}</TableCell>
										<TableCell>{t("dashboard.phone")}</TableCell>
										<TableCell align="right">{t("common.actions")}</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{getFilteredLeads().map((lead) => (
										<TableRow key={lead._id.$oid} hover>
											<TableCell>{lead.name}</TableCell>
											<TableCell>
												<Box sx={{ display: "flex", alignItems: "center" }}>
													<LeadQualityIcon
														quality={lead.leadQuality}
														size={isMobile ? "small" : "medium"}
														minimal
													/>
												</Box>
											</TableCell>
											<TableCell>
												{lead.statusName || lead.status ? (
													<Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
														<Box
															sx={{
																width: 10,
																height: 10,
																borderRadius: "50%",
																backgroundColor:
																	lead.statusColor ||
																	getColumnAccentColor(lead.status || ""),
																flexShrink: 0,
															}}
														/>
														<Typography
															variant={isMobile ? "body2" : "body2"}
															sx={{
																fontWeight: 600,
																color:
																	theme.palette.mode === "dark"
																		? "#CBD5E1"
																		: "#374151",
															}}
														>
															{translateStatus(
																lead.statusName || kanbanColumns[lead.status || ""],
																lead.statusId || lead.status
															)}
														</Typography>
													</Box>
												) : (
													"-"
												)}
											</TableCell>
											<TableCell>{getAssignedSellerName(lead)}</TableCell>
											<TableCell>{lead.phoneNumber}</TableCell>
											<TableCell align="right">
												<Button
													variant="outlined"
													size="small"
													onClick={() => setSelectedLead(lead)}
												>
													{t("dashboard.details")}
												</Button>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
						<Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
							<Button
								variant="contained"
								sx={{ color: theme.palette.mode === "dark" ? "#000" : undefined }}
								disabled={!assignedGlobalHasMore || assignedGlobalLoading}
								onClick={handleLoadMoreAssignedList}
							>
								{assignedGlobalLoading
									? t("dashboard.loading")
									: assignedGlobalHasMore
									? t("dashboard.loadMore")
									: t("dashboard.noMoreResults")}
							</Button>
						</Box>
					</Box>
				)}
			</Container>

			<LeadDetailModal
				open={!!selectedLead}
				lead={selectedLead}
				users={users}
				agentUsers={agentUsers}
				onClose={() => setSelectedLead(null)}
				onUpdateLead={handleUpdateLead}
				onAssignToMe={isSalesRole(user?.role || "") ? handleAssignLead : undefined}
				onDeleteLead={handleDeleteLead}
				onTransferLead={handleOpenTransferModal}
				isMobile={isMobile}
			/>

			<AssignLeadModal
				open={assignModalOpen}
				lead={leadToAssign}
				onClose={handleCloseAssignModal}
				onAssign={handleAssignToUser}
				users={users}
				isMobile={isMobile}
			/>

			<LeadAssignmentConfirmationModal
				open={confirmationModalOpen}
				lead={leadForConfirmation}
				leadsAssignedThisHour={getLeadsAssignedThisHour()}
				onClose={handleCloseConfirmationModal}
				onConfirm={handleConfirmLeadAssignment}
				isMobile={isMobile}
			/>

			<TransferLeadModal
				open={transferModalOpen}
				lead={leadToTransfer}
				onClose={handleCloseTransferModal}
				onTransfer={handleTransferLead}
				isMobile={isMobile}
			/>

			<CreateLeadModal
				open={createLeadModalOpen}
				onClose={() => setCreateLeadModalOpen(false)}
				onLeadCreated={handleLeadCreated}
				isMobile={isMobile}
			/>

			<DeleteLeadModal
				open={deleteModalOpen}
				lead={leadToDelete}
				onClose={handleCloseDeleteModal}
				onConfirm={handleConfirmDelete}
				isDeleting={isDeleting}
				isMobile={isMobile}
			/>

			{/* Custom Date Range Modal */}
			<Dialog
				open={customDateOpen}
				onClose={() => {
					setCustomDateOpen(false);
					if (!customStartDate && !customEndDate) setDateFilter("all");
				}}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle>{t("dashboard.customDateRange")}</DialogTitle>
				<DialogContent>
					<Box sx={{ display: "flex", gap: 2, mt: 1, flexDirection: isMobile ? "column" : "row" }}>
						<TextField
							size="small"
							type="date"
							label={t("dashboard.startDate")}
							value={customStartDate}
							onChange={(e) => setCustomStartDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
							fullWidth
						/>
						<TextField
							size="small"
							type="date"
							label={t("dashboard.endDate")}
							value={customEndDate}
							onChange={(e) => setCustomEndDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
							fullWidth
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setCustomDateOpen(false);
							if (!customStartDate && !customEndDate) setDateFilter("all");
						}}
					>
						{t("common.cancel")}
					</Button>
					<Button
						variant="contained"
						sx={{ color: theme.palette.mode === "dark" ? "#000" : undefined }}
						onClick={() => {
							setCustomDateOpen(false);
						}}
					>
						{t("dashboard.apply")}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Modal de plano inativo */}
			{canShowAlert("showPaymentAlerts") && !shouldHidePaymentElements && (
				<PlanInactiveModal
					open={planInactiveModalOpen}
					onClose={() => setPlanInactiveModalOpen(false)}
					onGoToPlans={handleGoToPlans}
				/>
			)}

			{/* Modal de boas-vindas para primeiro acesso */}
			{!shouldHidePaymentElements && (
				<FirstTimeWelcomeModal open={firstTimeWelcomeOpen} onClose={() => setFirstTimeWelcomeOpen(false)} />
			)}
		</Box>
	);
};

export default DashboardPage;
